import { useState } from "react";
import { LaunchReadinessForm } from "@/features/tools/LaunchReadinessForm";
import { LaunchReadinessResult } from "@/features/tools/LaunchReadinessResult";
import { ToolPageLayout } from "@/features/tools/ToolPageLayout";
import {
  generateLaunchReadinessChecklist,
  runLaunchReadinessChecker,
} from "@/features/tools/proToolApi";
import {
  toolPageMetadata,
} from "@/features/tools/toolMetadata";
import { useProToolPageData } from "@/features/tools/useProToolPageData";
import type {
  LaunchReadinessChecklistRequest,
  LaunchReadinessChecklistResponse,
  LaunchReadinessOutput,
  LaunchReadinessRequest,
} from "@/features/tools/proToolTypes";

const metadata = toolPageMetadata.launch_readiness_checker;

export function LaunchReadinessToolPage() {
  const {
    canUseProTools,
    results,
    projects,
    generatedBlueprints,
    isLoading,
    error,
    getToken,
    refreshResults,
  } = useProToolPageData();
  const [isGeneratingChecklist, setIsGeneratingChecklist] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [latestResult, setLatestResult] = useState<LaunchReadinessOutput | null>(
    null,
  );
  const [toolError, setToolError] = useState<string | null>(null);

  async function handleGenerateChecklist(
    input: LaunchReadinessChecklistRequest,
  ): Promise<LaunchReadinessChecklistResponse> {
    setIsGeneratingChecklist(true);
    setToolError(null);

    try {
      const token = await getToken();
      return await generateLaunchReadinessChecklist(token, input);
    } catch {
      const message = "We could not generate this right now. Please try again.";
      setToolError(message);
      throw new Error(message);
    } finally {
      setIsGeneratingChecklist(false);
    }
  }

  async function handleSubmit(input: LaunchReadinessRequest) {
    setIsSubmitting(true);
    setToolError(null);

    try {
      const token = await getToken();
      const response = await runLaunchReadinessChecker(token, input);
      setLatestResult(response.result.output_payload as LaunchReadinessOutput);
      await refreshResults();
    } catch {
      setToolError("We could not generate this right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ToolPageLayout
      title={metadata.title}
      label={metadata.label}
      intro={metadata.intro}
      canUseProTools={canUseProTools}
      isLoading={isLoading}
      error={error}
      results={results}
      toolType={metadata.toolType}
      emptyResultsMessage={metadata.emptyResultsMessage}
    >
      <LaunchReadinessForm
        projects={projects}
        generatedBlueprints={generatedBlueprints}
        isGeneratingChecklist={isGeneratingChecklist}
        isSubmitting={isSubmitting}
        error={toolError}
        onGenerateChecklist={handleGenerateChecklist}
        onSubmit={(input) => void handleSubmit(input)}
      />

      {latestResult ? <LaunchReadinessResult output={latestResult} /> : null}
    </ToolPageLayout>
  );
}
